import React, { useCallback, useEffect, useState, Component } from 'react';
import { Search, Eye, X, ChevronLeft, ChevronRight, Loader2, Copy, RefreshCw, AlertTriangle, Download, CheckCircle2, AlertCircle, Activity } from 'lucide-react';
import { useAuth } from '../store/authStore';
import { getAllSyncLogs } from '../api/syncLogs';
// --- Types ---
interface SyncLog {
  id: string;
  syncStartedAt: string;
  syncCompletedAt?: string;
  status: 'completed' | 'in_progress' | 'failed';
  recordsAttempted: number;
  recordsUploaded: number;
  recordsFailed: number;
  errorMessage?: string;
  device?: {
    id: string;
    deviceUid: string;
    deviceType: string;
  };
  sessionId?: string;
}
// --- Main Component ---
export function SyncLogs() {
  const {
    token
  } = useAuth();
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  // Filters & Pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // Modals & Panels State
  const [selectedLog, setSelectedLog] = useState<SyncLog | null>(null);
  const [toasts, setToasts] = useState<{
    id: string;
    message: string;
    type: 'success' | 'error';
  }[]>([]);
  // Live Timer State
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);
  // Fetch Data
  const fetchLogs = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const filters = {
        search: searchQuery,
        status: statusFilter === 'all' ? undefined : statusFilter,
        page: currentPage,
        limit: 10
      };
      const res = await getAllSyncLogs(token, filters);
      setLogs(res.logs || []);
      setTotalLogs(res.total || 0);
      setTotalPages(Math.ceil(res.total / 10));
    } catch (err: any) {
      setError(err.message || 'Failed to load sync logs');
    } finally {
      setIsLoading(false);
    }
  }, [token, searchQuery, statusFilter, currentPage]);
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, {
      id,
      message,
      type
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard');
  };
  const formatDuration = (start: string, end?: string) => {
    const startTime = new Date(start).getTime();
    const endTime = end ? new Date(end).getTime() : now.getTime();
    const diffMs = endTime - startTime;
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor(diffMs % 60000 / 1000);
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'failed':
        return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'in_progress':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
    }
  };
  return <div className="h-full flex flex-col" data-id="element-1002">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6" data-id="element-1003">
        <h1 className="text-2xl font-bold text-white" data-id="element-1004">Sync Logs</h1>
        <button className="flex items-center justify-center gap-2 bg-[#6B21E8] hover:bg-[#5818c2] text-white px-4 py-2 rounded-lg transition-colors w-full sm:w-auto" data-id="element-1005">
          <Download className="w-4 h-4" data-id="element-1006" />
          Export Logs
        </button>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6" data-id="element-1007">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto flex-1" data-id="element-1008">
          <div className="relative w-full sm:w-auto" data-id="element-1009">
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-1010" />
            <input type="text" placeholder="Search logs..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full sm:w-[280px] bg-[#1A1A24] border border-gray-800 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:border-[#6B21E8] transition-colors" data-id="element-1011" />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-1012">
            <Filter className="w-5 h-5 text-gray-400" data-id="element-1013" />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-[#1A1A24] border border-gray-800 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-[#6B21E8] transition-colors w-full sm:w-auto" data-id="element-1014">
              <option value="all" data-id="element-1015">All Status</option>
              <option value="success" data-id="element-1016">Success</option>
              <option value="failed" data-id="element-1017">Failed</option>
              <option value="in_progress" data-id="element-1018">In Progress</option>
            </select>
          </div>
        </div>

        <div className="text-sm text-gray-400 hidden sm:block" data-id="element-1019">
          Showing {(currentPage - 1) * 10 + 1}-
          {Math.min(currentPage * 10, totalLogs)} of {totalLogs}
        </div>
      </div>

      <div className="bg-[#1A1A24] border border-gray-800 rounded-xl flex-1 flex flex-col overflow-hidden" data-id="element-1020">
        <div className="overflow-x-auto flex-1" data-id="element-1021">
          <table className="w-full text-sm" data-id="element-1022">
            <thead data-id="element-1023">
              <tr className="border-b border-gray-800" data-id="element-1024">
                <th className="text-left py-4 px-4 font-medium text-gray-400" data-id="element-1025">
                  Log ID
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-400" data-id="element-1026">
                  Status
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-400 hidden md:table-cell" data-id="element-1027">
                  Records
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-400" data-id="element-1028">
                  Started
                </th>
                <th className="text-left py-4 px-4 font-medium text-gray-400 hidden md:table-cell" data-id="element-1029">
                  Error
                </th>
                <th className="text-right py-4 px-4 font-medium text-gray-400" data-id="element-1030">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1031">
              {isLoading ? <tr data-id="element-1032">
                  <td colSpan={6} className="text-center py-8 text-gray-400" data-id="element-1033">
                    <div className="flex items-center justify-center gap-2" data-id="element-1034">
                      <div className="w-5 h-5 border-2 border-[#6B21E8] border-t-transparent rounded-full animate-spin" data-id="element-1035" />
                      Loading logs...
                    </div>
                  </td>
                </tr> : logs.length === 0 ? <tr data-id="element-1036">
                  <td colSpan={6} className="text-center py-8 text-gray-400" data-id="element-1037">
                    No logs found matching your criteria.
                  </td>
                </tr> : logs.map(log => <tr key={log.id} className="border-b border-gray-800 hover:bg-gray-900/50 transition-colors" data-id="element-1038">
                    <td className="py-4 px-4" data-id="element-1039">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(log.status)}`} data-id="element-1040">
                        {log.status === 'success' && <CheckCircle2 className="w-3.5 h-3.5" data-id="element-1041" />}
                        {log.status === 'failed' && <AlertCircle className="w-3.5 h-3.5" data-id="element-1042" />}
                        {log.status === 'in_progress' && <Activity className="w-3.5 h-3.5" data-id="element-1043" />}
                        {log.status.replace('_', ' ').charAt(0).toUpperCase() + log.status.slice(1).replace('_', ' ')}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-gray-300 hidden md:table-cell" data-id="element-1044">
                      {log.recordsProcessed} / {log.recordsTotal}
                    </td>
                    <td className="py-4 px-4 text-gray-300" data-id="element-1045">
                      {new Date(log.startedAt).toLocaleString()}
                    </td>
                    <td className="py-4 px-4 hidden md:table-cell" data-id="element-1046">
                      {log.errorMessage ? <span className="text-red-400 text-xs truncate max-w-[200px] block" title={log.errorMessage} data-id="element-1047">
                          {log.errorMessage}
                        </span> : <span className="text-gray-500" data-id="element-1048">-</span>}
                    </td>
                    <td className="py-4 px-4 text-right" data-id="element-1049">
                      <button onClick={() => setSelectedLog(log)} className="text-[#6B21E8] hover:text-white transition-colors" data-id="element-1050">
                        <Eye className="w-4 h-4" data-id="element-1051" />
                      </button>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-gray-800" data-id="element-1052">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-400" data-id="element-1053">
            <div className="sm:hidden" data-id="element-1054">
              Showing {(currentPage - 1) * 10 + 1}-
              {Math.min(currentPage * 10, totalLogs)} of {totalLogs}
            </div>
            <div className="flex items-center gap-2" data-id="element-1055">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="p-2 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1056">
                <ChevronLeft className="w-5 h-5" data-id="element-1057" />
              </button>
              <span className="text-white font-medium" data-id="element-1058">
                Page {currentPage} of {totalPages}
              </span>
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="p-2 hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1059">
                <ChevronRight className="w-5 h-5" data-id="element-1060" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Panel */}
      {selectedLog && <div className="fixed inset-y-0 right-0 z-50 flex" data-id="element-1061">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSelectedLog(null)} data-id="element-1062" />
          <div className="relative w-full max-w-[460px] h-full bg-[#1A1A24] border-l border-gray-800 flex flex-col shadow-2xl ml-auto" data-id="element-1063">
            <div className="flex items-center justify-between p-6 border-b border-gray-800" data-id="element-1064">
              <h2 className="text-xl font-bold text-white" data-id="element-1065">Log Details</h2>
              <button onClick={() => setSelectedLog(null)} className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white" data-id="element-1066">
                <X className="w-5 h-5" data-id="element-1067" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-1068">
              <div className="space-y-4" data-id="element-1069">
                <h3 className="text-sm font-medium text-gray-400" data-id="element-1070">
                  Information
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-1071">
                  <div data-id="element-1072">
                    <p className="text-xs text-gray-500" data-id="element-1073">Log ID</p>
                    <p className="text-sm font-mono text-white" data-id="element-1074">
                      {selectedLog.id}
                    </p>
                  </div>
                  <div data-id="element-1075">
                    <p className="text-xs text-gray-500" data-id="element-1076">Status</p>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedLog.status)}`} data-id="element-1077">
                      {selectedLog.status === 'success' && <CheckCircle2 className="w-3.5 h-3.5" data-id="element-1078" />}
                      {selectedLog.status === 'failed' && <AlertCircle className="w-3.5 h-3.5" data-id="element-1079" />}
                      {selectedLog.status === 'in_progress' && <Activity className="w-3.5 h-3.5" data-id="element-1080" />}
                      {selectedLog.status.replace('_', ' ').charAt(0).toUpperCase() + selectedLog.status.slice(1).replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-4" data-id="element-1081">
                <h3 className="text-sm font-medium text-gray-400" data-id="element-1082">Records</h3>
                <div className="grid grid-cols-3 gap-4" data-id="element-1083">
                  <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-3 text-center" data-id="element-1084">
                    <p className="text-xs text-gray-500" data-id="element-1085">Attempted</p>
                    <p className="text-sm font-mono text-white" data-id="element-1086">
                      {selectedLog.recordsAttempted}
                    </p>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center" data-id="element-1087">
                    <p className="text-xs text-green-500" data-id="element-1088">Uploaded</p>
                    <p className="text-sm font-mono text-green-400" data-id="element-1089">
                      {selectedLog.recordsUploaded}
                    </p>
                  </div>
                  <div className={`bg-red-500/10 border rounded-lg p-3 text-center ${selectedLog.recordsFailed > 0 ? 'border-red-500/30' : 'border-gray-800'}`} data-id="element-1090">
                    <p className={`text-xs uppercase mb-1 ${selectedLog.recordsFailed > 0 ? 'text-red-500' : 'text-gray-500'}`} data-id="element-1091">
                      Failed
                    </p>
                    <p className={`text-sm font-mono ${selectedLog.recordsFailed > 0 ? 'text-red-400' : 'text-gray-400'}`} data-id="element-1092">
                      {selectedLog.recordsFailed}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4" data-id="element-1093">
                <h3 className="text-sm font-medium text-gray-400" data-id="element-1094">
                  Error Details
                </h3>
                {selectedLog.errorMessage ? <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-id="element-1095">
                    <pre className="text-xs text-red-400 font-mono whitespace-pre-wrap break-words" data-id="element-1096">
                      {selectedLog.errorMessage}
                    </pre>
                  </div> : <div className="text-gray-500 text-sm" data-id="element-1097">
                    No error details available
                  </div>}
              </div>
            </div>
          </div>
        </div>}

      {/* Toasts */}
      <div className="fixed top-20 right-4 sm:top-6 sm:right-6 z-50 flex flex-col gap-2" data-id="element-1098">
        {toasts.map(toast => <div key={toast.id} className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border ${toast.type === 'success' ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`} data-id="element-1099">
            <div className={`w-2 h-2 rounded-full ${toast.type === 'success' ? 'bg-green-400 animate-pulse' : 'bg-red-400 animate-pulse'}`} data-id="element-1100" />
            <span className="text-sm" data-id="element-1101">{toast.message}</span>
          </div>)}
      </div>
    </div>;
}
// --- Subcomponents ---
function SyncLogDetailPanel({
  log,
  onClose,
  now
}: {
  log: SyncLog | null;
  onClose: () => void;
  now: Date;
}) {
  if (!log) return null;
  const formatDuration = (start: string, end?: string) => {
    const startTime = new Date(start).getTime();
    const endTime = end ? new Date(end).getTime() : now.getTime();
    const diffMs = endTime - startTime;
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor(diffMs % 60000 / 1000);
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };
  return <div className="fixed inset-0 z-40 flex justify-end" data-id="element-1102">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} data-id="element-1103" />
      <div className="relative w-[400px] h-full bg-surface border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-250" data-id="element-1104">
        {/* Header */}
        <div className="p-6 border-b border-border relative" data-id="element-1105">
          <button onClick={onClose} className="absolute top-6 right-6 text-text-secondary hover:text-white transition-colors" data-id="element-1106">
            <X className="w-5 h-5" data-id="element-1107" />
          </button>
          <div className="flex items-center gap-4" data-id="element-1108">
            <div className="w-12 h-12 rounded-full bg-[rgba(245,158,11,0.1)] flex items-center justify-center flex-shrink-0" data-id="element-1109">
              <RefreshCw className="w-6 h-6 text-warning" data-id="element-1110" />
            </div>
            <div data-id="element-1111">
              <h2 className="text-[18px] font-semibold text-white" data-id="element-1112">
                Sync Log Detail
              </h2>
              <p className="text-[13px] text-text-secondary font-mono mt-1" data-id="element-1113">
                {log.id}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-8" data-id="element-1114">
          {/* Info */}
          <div className="space-y-4" data-id="element-1115">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-1116">
              Information
            </h3>
            <div className="space-y-3" data-id="element-1117">
              <div className="flex justify-between items-center" data-id="element-1118">
                <span className="text-[13px] text-text-secondary" data-id="element-1119">Status</span>
                {log.status === 'completed' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(34,197,94,0.1)] text-success text-[12px] border border-[rgba(34,197,94,0.2)]" data-id="element-1120">
                    Completed
                  </span>}
                {log.status === 'in_progress' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(245,158,11,0.1)] text-warning text-[12px] border border-[rgba(245,158,11,0.2)]" data-id="element-1121">
                    In Progress
                  </span>}
                {log.status === 'failed' && <span className="px-2 py-0.5 rounded-badge bg-[rgba(239,68,68,0.1)] text-danger text-[12px] border border-[rgba(239,68,68,0.2)]" data-id="element-1122">
                    Failed
                  </span>}
              </div>
              <div className="flex justify-between" data-id="element-1123">
                <span className="text-[13px] text-text-secondary" data-id="element-1124">Started</span>
                <span className="text-[13px] text-white" data-id="element-1125">
                  {new Date(log.syncStartedAt).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-1126">
                <span className="text-[13px] text-text-secondary" data-id="element-1127">
                  Completed
                </span>
                <span className="text-[13px] text-white" data-id="element-1128">
                  {log.syncCompletedAt ? new Date(log.syncCompletedAt).toLocaleString() : '—'}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-1129">
                <span className="text-[13px] text-text-secondary" data-id="element-1130">
                  Duration
                </span>
                <span className="text-[13px] text-white" data-id="element-1131">
                  {formatDuration(log.syncStartedAt, log.syncCompletedAt)}
                </span>
              </div>
            </div>
          </div>

          {/* Records Stats */}
          <div className="space-y-4" data-id="element-1132">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-1133">
              Records
            </h3>
            <div className="grid grid-cols-3 gap-3" data-id="element-1134">
              <div className="bg-background border border-border rounded-card p-3 text-center" data-id="element-1135">
                <p className="text-[11px] text-text-secondary uppercase mb-1" data-id="element-1136">
                  Attempted
                </p>
                <p className="text-[18px] font-semibold text-white" data-id="element-1137">
                  {log.recordsAttempted}
                </p>
              </div>
              <div className="bg-background border border-[rgba(34,197,94,0.3)] rounded-card p-3 text-center" data-id="element-1138">
                <p className="text-[11px] text-success uppercase mb-1" data-id="element-1139">
                  Uploaded
                </p>
                <p className="text-[18px] font-semibold text-success" data-id="element-1140">
                  {log.recordsUploaded}
                </p>
              </div>
              <div className={`bg-background border rounded-card p-3 text-center ${log.recordsFailed > 0 ? 'border-[rgba(239,68,68,0.3)]' : 'border-border'}`} data-id="element-1141">
                <p className={`text-[11px] uppercase mb-1 ${log.recordsFailed > 0 ? 'text-danger' : 'text-text-secondary'}`} data-id="element-1142">
                  Failed
                </p>
                <p className={`text-[18px] font-semibold ${log.recordsFailed > 0 ? 'text-danger' : 'text-white'}`} data-id="element-1143">
                  {log.recordsFailed}
                </p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {log.errorMessage && <div className="space-y-4" data-id="element-1144">
              <h3 className="text-[12px] uppercase font-semibold text-danger tracking-[1px] flex items-center gap-2" data-id="element-1145">
                <AlertTriangle className="w-3 h-3" data-id="element-1146" /> Error Details
              </h3>
              <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-[8px] p-3 max-h-[200px] overflow-y-auto" data-id="element-1147">
                <pre className="text-[12px] text-danger font-mono whitespace-pre-wrap break-words" data-id="element-1148">
                  {log.errorMessage}
                </pre>
              </div>
            </div>}

          {/* Entities */}
          <div className="space-y-4" data-id="element-1149">
            <h3 className="text-[12px] uppercase font-semibold text-text-secondary tracking-[1px]" data-id="element-1150">
              Related Entities
            </h3>

            {log.device && <div className="bg-background border border-border rounded-card p-3 flex justify-between items-center" data-id="element-1151">
                <div data-id="element-1152">
                  <p className="text-[11px] text-text-secondary mb-1" data-id="element-1153">Device</p>
                  <p className="text-[13px] text-white font-mono" data-id="element-1154">
                    {log.device.deviceUid}
                  </p>
                  <p className="text-[11px] text-text-tertiary" data-id="element-1155">
                    {log.device.deviceType || 'Bella Belt'}
                  </p>
                </div>
                <button className="text-[12px] text-accent hover:underline" data-id="element-1156">
                  View
                </button>
              </div>}

            {log.sessionId && <div className="bg-background border border-border rounded-card p-3 flex justify-between items-center" data-id="element-1157">
                <div data-id="element-1158">
                  <p className="text-[11px] text-text-secondary mb-1" data-id="element-1159">
                    Session
                  </p>
                  <p className="text-[13px] text-white font-mono" data-id="element-1160">
                    {log.sessionId.substring(0, 8)}...
                  </p>
                </div>
                <button className="text-[12px] text-accent hover:underline" data-id="element-1161">
                  View Session
                </button>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}